import { useId } from 'react';
import { PiCalculatorDuotone, PiCheckDuotone, PiFilePlusDuotone, PiFileTextDuotone, PiFlagDuotone, PiEnvelopeDuotone, PiPlayDuotone, PiPlusDuotone } from 'react-icons/pi';
import { Skeleton, Table } from './ui';
import './FormSkeleton.css';

// Step-type icons for the route, in the same visual language as the workflow canvas.
const ROUTE_NODES = [
  { x: 24, Icon: PiPlayDuotone, state: 'done', tone: 'start' },
  { x: 72, Icon: PiCalculatorDuotone, state: 'done' },
  { x: 120, Icon: PiFileTextDuotone, state: 'current' },
  { x: 168, Icon: PiEnvelopeDuotone, state: 'ahead' },
  { x: 216, Icon: PiFlagDuotone, state: 'ahead' },
];
const ROUTE_Y = 75;
const NODE_SIZE = 30;
const NODE_HALF = NODE_SIZE / 2;
// Each step's wave on load trails the previous one by this much.
const WAVE_STAGGER = 90;
const wave = idx => ({ animationDelay: `${idx * WAVE_STAGGER}ms` });

function CheckBadge({ x, y, className }) {
  return (
    <g className={className}>
      <circle cx={x} cy={y} r="4.5" className="fs-check" />
      <PiCheckDuotone x={x - 2.75} y={y - 2.75} size={5.5} className="fs-check-mark" />
    </g>
  );
}

/** Miniature form card (title, two fields, submit), centered on (cx, cy). */
function FormOutline({ cx, cy, className }) {
  const x = cx - 11;
  const y = cy - 14;
  return (
    <g className={className}>
      <rect x={x} y={y} width="22" height="28" rx="2.5" className="fs-form-card" />
      <rect x={x + 3} y={y + 3.5} width="10" height="1.8" rx="0.9" className="fs-form-title" />
      {[0, 1].map(i => (
        <g key={i}>
          <rect x={x + 3} y={y + 8.5 + i * 7} width="7" height="1.1" rx="0.55" className="fs-bar" />
          <rect x={x + 3} y={y + 10.5 + i * 7} width="16" height="3.2" rx="0.8" className="fs-form-input" />
          <rect x={x + 3.8} y={y + 11.6 + i * 7} width={[9, 6][i]} height="1" rx="0.5" className={`fs-form-fill fs-form-fill--${i + 1}`} />
        </g>
      ))}
      <rect x={x + 3} y={y + 23} width="8" height="2.6" rx="1.3" className="fs-form-fill fs-form-submit" />
    </g>
  );
}

/**
 * details/view: the record's route through its workflow. A token travels from the start step past
 * the completed ones (each checked off as it passes) to the current step, then the view zooms into
 * that step as it opens into the form — "this form is where your record is in the workflow".
 */
function WorkflowRouteIllustration() {
  const current = ROUTE_NODES.findIndex(n => n.state === 'current');
  const link = idx => `M ${ROUTE_NODES[idx].x + NODE_HALF + 1} ${ROUTE_Y} H ${ROUTE_NODES[idx + 1].x - NODE_HALF - 1}`;
  return (
    <svg viewBox="0 0 240 150" className="form-step-illustration" aria-hidden="true">
      <g className="fsr-scene">
        <g className="fsr-context">
          {ROUTE_NODES.slice(1).map((_, idx) => (
            <path key={idx} d={link(idx)} className="fs-link" />
          ))}
          <path d={link(0)} pathLength={100} className="fs-link-done fsr-link-1" />
          <path d={link(1)} pathLength={100} className="fs-link-done fsr-link-2" />

          {ROUTE_NODES.map(({ x, Icon, state, tone }, idx) =>
            state === 'current' ? null : (
              <g key={x} className={state === 'ahead' ? 'fs-node fs-node--ahead fsr-wave' : 'fs-node fsr-wave'} style={wave(idx)}>
                <rect x={x - NODE_HALF} y={ROUTE_Y - NODE_HALF} width={NODE_SIZE} height={NODE_SIZE} rx="7.5" className="fs-node-card" />
                <Icon x={x - 7.5} y={ROUTE_Y - 7.5} size={15} className={tone === 'start' ? 'fs-green' : 'fs-muted'} />
              </g>
            )
          )}
          <CheckBadge x={ROUTE_NODES[0].x + 12} y={ROUTE_Y - 12} className="fs-pop fsr-check-1" />
          <CheckBadge x={ROUTE_NODES[1].x + 12} y={ROUTE_Y - 12} className="fs-pop fsr-check-2" />
        </g>

        {/* The current step: waves with the rest, pulses when the record arrives, then opens into the form. */}
        <rect x={120 - NODE_HALF - 4} y={ROUTE_Y - NODE_HALF - 4} width={NODE_SIZE + 8} height={NODE_SIZE + 8} rx="10" className="fsr-arrival-ring" />
        <g className="fsr-wave" style={wave(current)}>
          <g className="fsr-current-node">
            <rect
              x={120 - NODE_HALF}
              y={ROUTE_Y - NODE_HALF}
              width={NODE_SIZE}
              height={NODE_SIZE}
              rx="7.5"
              className="fs-node-card fs-node-card--current"
            />
            <PiFileTextDuotone x={112.5} y={ROUTE_Y - 7.5} size={15} className="fs-primary" />
          </g>
        </g>
        <FormOutline cx={120} cy={ROUTE_Y} className="fsr-form" />

        <g className="fsr-token">
          <circle cx={ROUTE_NODES[0].x} cy={ROUTE_Y} r="7" className="fs-token-halo" />
          <circle cx={ROUTE_NODES[0].x} cy={ROUTE_Y} r="3.5" className="fs-token" />
        </g>
      </g>
    </svg>
  );
}

// The start step is drawn at this scale, centered, so it reads as the one thing on screen.
const START_SCALE = 1.85;

/**
 * new: a workflow being started. The "new" button is pressed, a fresh record appears on the start
 * step (green start badge and ring, as on the canvas) and the line toward the next step extends
 * off to the right, then the record flips open into the first form.
 */
function WorkflowStartIllustration() {
  const cx = 120;
  const fadeId = `${useId().replace(/:/g, '')}-route-fade`;
  return (
    <svg viewBox="0 0 240 150" className="form-step-illustration" aria-hidden="true">
      <defs>
        <linearGradient id={fadeId} gradientUnits="userSpaceOnUse" x1={cx + 18 * START_SCALE} y1="0" x2="240" y2="0">
          <stop offset="0" className="fss-route-stop" />
          <stop offset="0.55" className="fss-route-stop" />
          <stop offset="1" className="fss-route-stop fss-route-stop--end" />
        </linearGradient>
      </defs>
      <g className="fss-scene">
        {/* The line on to the next step, which is off screen — fading out toward the edge. */}
        <path
          d={`M ${cx + 18 * START_SCALE} ${ROUTE_Y} H 240`}
          pathLength={100}
          className="fs-link fss-route"
          style={{ stroke: `url(#${fadeId})` }}
        />

        <g className="fs-svg-transform" transform={`translate(${cx} ${ROUTE_Y}) scale(${START_SCALE}) translate(${-cx} ${-ROUTE_Y})`}>
          {/* Pressing "new": ripples and sparks as the record is created. */}
          <circle cx={cx} cy={ROUTE_Y} r="11" className="fss-ripple fss-ripple-1" />
          <circle cx={cx} cy={ROUTE_Y} r="11" className="fss-ripple fss-ripple-2" />
          {[
            [-22, -16],
            [22, -18],
            [-20, 18],
            [24, 14],
          ].map(([dx, dy], idx) => (
            <circle key={idx} cx={cx} cy={ROUTE_Y} r="1.8" className="fss-spark" style={{ '--spark-x': `${dx}px`, '--spark-y': `${dy}px` }} />
          ))}
          <g className="fss-plus">
            <circle cx={cx} cy={ROUTE_Y} r="11" className="fs-plus-button" />
            <PiPlusDuotone x={cx - 6} y={ROUTE_Y - 6} size={12} className="fs-on-primary" />
          </g>

          {/* The new record, sitting on the start step. */}
          <g className="fss-record">
            <rect x={cx - 17} y={ROUTE_Y - 21} width="34" height="42" rx="5" className="fss-start-ring" />
            <rect x={cx - 15} y={ROUTE_Y - 19} width="30" height="38" rx="4" className="fs-node-card" />
            <PiFilePlusDuotone x={cx - 7} y={ROUTE_Y - 10} size={14} className="fs-primary" />
            <rect x={cx - 8} y={ROUTE_Y + 8} width="16" height="2" rx="1" className="fs-bar" />
            <g className="fss-start-badge">
              <rect x={cx - 13} y={ROUTE_Y - 24} width="18" height="7" rx="3.5" className="fs-green-fill" />
              <PiPlayDuotone x={cx - 11} y={ROUTE_Y - 22.75} size={4.5} className="fs-on-primary fs-play" />
              <rect x={cx - 5} y={ROUTE_Y - 21.25} width="7" height="1.5" rx="0.75" className="fs-on-primary-bar" />
            </g>
          </g>
          <g className="fss-form">
            <g className="fs-svg-transform" transform={`translate(${cx} ${ROUTE_Y}) scale(1.4) translate(${-cx} ${-ROUTE_Y})`}>
              <FormOutline cx={cx} cy={ROUTE_Y} />
            </g>
          </g>
        </g>
      </g>
    </svg>
  );
}

// Reused label+control pairing so every fake field keeps the same vertical rhythm.
function SkeletonField({ labelWidth = '40%', children }) {
  return (
    <div className="mb-4">
      <Skeleton width={labelWidth} height="0.75rem" className="mb-2" />
      {children}
    </div>
  );
}

function SkeletonChoiceRow({ shape = 'square', count = 3 }) {
  return (
    <div className="flex items-center gap-4">
      {Array.from({ length: count }).map((_, i) => (
        <div key={i} className="flex items-center gap-2">
          <Skeleton width="1rem" height="1rem" rounded={shape === 'circle' ? 'full' : 'sm'} />
          <Skeleton width={`${60 + ((i * 11) % 30)}px`} height="0.75rem" />
        </div>
      ))}
    </div>
  );
}

// A single fake table cell, shaped like one of a few common input types so a
// row doesn't read as a wall of identical gray bars.
function SkeletonCell({ variant = 'text', seed = 0 }) {
  if (variant === 'radio') {
    return (
      <div className="flex items-center gap-3">
        {Array.from({ length: 2 }).map((_, i) => (
          <div key={i} className="flex items-center gap-1">
            <Skeleton width="0.875rem" height="0.875rem" rounded="full" />
            <Skeleton width="2rem" height="0.7rem" />
          </div>
        ))}
      </div>
    );
  }

  if (variant === 'select') {
    return (
      <div className="flex items-center justify-between gap-2">
        <Skeleton width={`${55 + ((seed * 13) % 30)}%`} height="0.875rem" />
        <Skeleton width="0.6rem" height="0.6rem" rounded="sm" />
      </div>
    );
  }

  return <Skeleton width={`${45 + ((seed * 17) % 45)}%`} height="0.875rem" />;
}

// Mimics ArrayFieldTemplate's default table layout (title, header row, item
// rows with a trailing row-toolbox column, "Add Item" affordance at the end).
function SkeletonArraySection() {
  const columns = 4;
  const rows = 2;

  return (
    <div className="mb-5">
      <Skeleton width="30%" height="0.875rem" className="mb-2" />
      <Table className="animated-show" responsive>
        <thead>
          <tr>
            {Array.from({ length: columns }).map((_, i) => (
              <th key={i}>
                <Skeleton width={`${50 + ((i * 9) % 30)}%`} height="0.75rem" />
              </th>
            ))}
            <th style={{ width: 1 }} />
          </tr>
        </thead>
        <tbody>
          {Array.from({ length: rows }).map((_, rowIdx) => (
            <tr key={rowIdx}>
              {Array.from({ length: columns }).map((_, colIdx) => (
                <td key={colIdx}>
                  <SkeletonCell variant={colIdx === columns - 1 ? 'select' : 'text'} seed={rowIdx + colIdx} />
                </td>
              ))}
              <td>
                <div className="flex items-center gap-2 justify-center">
                  <Skeleton width="1.25rem" height="1.25rem" rounded="sm" />
                  <Skeleton width="1.25rem" height="1.25rem" rounded="sm" />
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </Table>
      <Skeleton width="6.5rem" height="2rem" rounded="lg" className="mt-3" />
    </div>
  );
}

// Mimics the full-width "object-as-table" layout (ObjectTable): one row per
// property, a mix of cell shapes across the columns for visual variety.
function SkeletonObjectTableSection() {
  const columns = 5;
  const rows = 4;
  const variantForColumn = colIdx => (colIdx === 1 ? 'radio' : colIdx === columns - 1 ? 'select' : 'text');

  return (
    <div>
      <Skeleton width="25%" height="0.875rem" className="mb-2" />
      <Table className="animated-show" responsive>
        <thead>
          <tr>
            {Array.from({ length: columns }).map((_, i) => (
              <th key={i}>
                <Skeleton width={`${45 + ((i * 11) % 35)}%`} height="0.75rem" />
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {Array.from({ length: rows }).map((_, rowIdx) => (
            <tr key={rowIdx}>
              {Array.from({ length: columns }).map((_, colIdx) => (
                <td key={colIdx}>
                  <SkeletonCell variant={variantForColumn(colIdx)} seed={rowIdx + colIdx} />
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </Table>
    </div>
  );
}

/** Generic, schema-agnostic form placeholder shown while a workflow step's form is loading.
 *  Same shape every time — it only needs to read as "a form is coming," not mimic the real one.
 *  `title`/`message` (optional React nodes) render an illustration + status text over the
 *  dimmed skeleton, so callers along the loading chain can surface what's currently happening
 *  (e.g. "Loading your record..." vs "Preparing your form..."). `variant="start"` swaps the
 *  "arriving at your step" route for the "starting a new workflow" illustration.
 */
function FormSkeleton({ title, message, variant = 'route' }) {
  return (
    <div className="form-loading-overlay-wrapper overflow-hidden">
      <div aria-hidden="true" style={{ padding: '15px 32px 15px 15px', opacity: '0.2' }}>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
          <SkeletonField labelWidth="60%">
            <Skeleton height="2.125rem" rounded="lg" />
          </SkeletonField>
          <SkeletonField labelWidth="45%">
            <Skeleton height="2.125rem" rounded="lg" />
          </SkeletonField>
          <SkeletonField labelWidth="70%">
            <Skeleton height="2.125rem" rounded="lg" />
          </SkeletonField>
        </div>

        <SkeletonField labelWidth="50%">
          <Skeleton height="5rem" rounded="lg" />
        </SkeletonField>

        <div className="flex items-center gap-8 mb-5 justify-between">
          <div className="">
            <Skeleton width="70%" height="0.75rem" className="mb-2" />
            <SkeletonChoiceRow shape="square" count={3} />
          </div>

          <div className="">
            <Skeleton width="70%" height="0.75rem" className="mb-2" />
            <SkeletonChoiceRow shape="square" count={3} />
          </div>

          <div className="">
            <Skeleton width="65%" height="0.75rem" className="mb-2" />
            <SkeletonChoiceRow shape="circle" count={2} />
          </div>
        </div>

        <SkeletonArraySection />

        <SkeletonObjectTableSection />
      </div>

      {(title || message) && (
        <div className="form-loading-overlay">
          <div className="form-loading-panel py-6 px-12 rounded-xl shadow">
            {variant === 'start' ? <WorkflowStartIllustration /> : <WorkflowRouteIllustration />}
            {title ? <p className="form-loading-title">{title}</p> : null}
            {message ? <p className="form-loading-subtitle">{message}</p> : null}
          </div>
        </div>
      )}
    </div>
  );
}

export default FormSkeleton;
